import React, { useState, useEffect, useRef } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import { noteColors } from './noteColors';
import { Note } from 'tonal';
import { getThreeJsColor } from '../utils/daisyColors';
import { Color, Group } from 'three';

interface PianoKeysProps {
  activeNotes: string[];
  octaves?: number;
  showNoteLetters?: boolean;
}

const PianoKeys: React.FC<PianoKeysProps> = ({ activeNotes, octaves = 1, showNoteLetters = true }) => {
  const [fretboardColor, setFretboardColor] = useState(getThreeJsColor('dark', 'base-100'));
  const [fretColor, setFretColor] = useState(getThreeJsColor('dark', 'base-content'));
  const [keyStrokeColor, setKeyStrokeColor] = useState(getThreeJsColor('dark', 'base-100'));

  useEffect(() => {
    const updateColors = () => {
      setFretboardColor(getThreeJsColor('dark', 'base-100'));
      setFretColor(getThreeJsColor('dark', 'base-content'));
      setKeyStrokeColor(getThreeJsColor('dark', 'base-300'));
    };

    document.addEventListener('daisyui-theme-change', updateColors);

    return () => {
      document.removeEventListener('daisyui-theme-change', updateColors);
    };
  }, []);

  return (
    <div className="w-full h-16 md:h-32 mb-2">
      <Canvas
        orthographic
        camera={{ zoom: 1, position: [0, 0, 100], near: 1, far: 1000 }}
        style={{ width: '100%', height: '100%' }}
      >
        <PianoKeysSizer
          activeNotes={activeNotes}
          octaves={octaves}
          showNoteLetters={showNoteLetters}
          fretboardColor={fretboardColor}
          fretColor={fretColor}
          keyStrokeColor={keyStrokeColor}
        />
      </Canvas>
    </div>
  );
};

interface PianoKeysSizerProps extends PianoKeysProps {
  fretboardColor: Color;
  fretColor: Color;
  keyStrokeColor: Color;
}

const PianoKeysSizer: React.FC<PianoKeysSizerProps> = (props) => {
  const { size } = useThree();
  const ref = useRef<Group>(null);

  const WHITE_KEY_WIDTH = 0.3;
  const WHITE_KEY_HEIGHT = 0.7;
  const KEY_GAP = 0.015;

  // Calculate total width of one octave
  const OCTAVE_WIDTH = WHITE_KEY_WIDTH * 7 + KEY_GAP * 7;
  // Calculate total width of all octaves
  const TOTAL_WIDTH = OCTAVE_WIDTH * props.octaves;
  // Calculate total height
  const TOTAL_HEIGHT = WHITE_KEY_HEIGHT;

  useEffect(() => {
    const handleResize = () => {
      if (ref.current) {
        const scaleWidth = size.width / TOTAL_WIDTH;
        const scaleHeight = size.height / TOTAL_HEIGHT;
        const scale = Math.min(scaleWidth, scaleHeight) * 0.95; // Add 10% margin

        ref.current.scale.set(scale, scale, 1);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial sizing

    return () => window.removeEventListener('resize', handleResize);
  }, [size, TOTAL_WIDTH, TOTAL_HEIGHT]);

  return (
    <group ref={ref} position={[0, 0, 0]}>
      <PianoKeysContent {...props} />
    </group>
  );
};

interface PianoKeysContentProps extends PianoKeysSizerProps {}

const PianoKeysContent: React.FC<PianoKeysContentProps> = ({
  activeNotes,
  octaves,
  showNoteLetters,
  fretboardColor,
  fretColor,
  keyStrokeColor
}) => {
  const baseNotes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const allNotes = Array.from({ length: octaves }, (_, octave) => 
    baseNotes.map(note => `${note}${octave + 4}`)
  ).flat();
  
  const isBlackKey = (note: string) => note.includes('#');
  
  const getNoteColor = (note: string) => {
    // Strip octave number from the note
    const noteWithoutOctave = note.replace(/\d+$/, '');
    
    // Check if this note or its enharmonic equivalent is in activeNotes
    const isActive = activeNotes.some(activeNote => {
      const activeNoteWithoutOctave = activeNote.replace(/\d+$/, '');
      const normalizedActiveNote = Note.get(activeNoteWithoutOctave).pc;
      const normalizedNote = Note.get(noteWithoutOctave).pc;
      return normalizedActiveNote === normalizedNote;
    });
    
    if (isActive) {
      // Find the matching note from activeNotes
      const matchingNote = activeNotes.find(activeNote => {
        const activeNoteWithoutOctave = activeNote.replace(/\d+$/, '');
        const normalizedActiveNote = Note.get(activeNoteWithoutOctave).pc;
        const normalizedNote = Note.get(noteWithoutOctave).pc;
        return normalizedActiveNote === normalizedNote;
      });

      if (matchingNote) {
        const noteForColor = matchingNote.replace(/\d+$/, '');
        return noteColors[noteForColor] || '#E0E0E0';
      }
    }
    
    // For inactive notes, use the appropriate color based on key type
    return isBlackKey(noteWithoutOctave) 
      ? `#${fretboardColor.getHexString()}`
      : `#${fretColor.getHexString()}`;
  };

  const getDisplayNote = (note: string) => {
    const noteWithoutOctave = note.replace(/\d+$/, '');
    
    // Find the matching note from activeNotes
    const matchingNote = activeNotes.find(activeNote => {
      const activeNoteWithoutOctave = activeNote.replace(/\d+$/, '');
      return activeNoteWithoutOctave === noteWithoutOctave || 
             Note.enharmonic(activeNoteWithoutOctave) === noteWithoutOctave;
    });

    return matchingNote ? matchingNote.replace(/\d+$/, '') : noteWithoutOctave;
  };

  const WHITE_KEY_WIDTH = 0.3;
  const BLACK_KEY_WIDTH = 0.2;
  const WHITE_KEY_HEIGHT = 0.7;
  const BLACK_KEY_HEIGHT = 0.425;
  const KEY_DEPTH = 0.05;
  const KEY_GAP = 0.015;

  // Calculate total width of one octave
  const OCTAVE_WIDTH = WHITE_KEY_WIDTH * 7 + KEY_GAP * 7;
  // Calculate total width of all octaves
  const TOTAL_WIDTH = OCTAVE_WIDTH * octaves;
  // Calculate offset to center the keyboard
  const CENTER_OFFSET = (TOTAL_WIDTH - WHITE_KEY_WIDTH) / 2;

  return (
    <group position={[-CENTER_OFFSET, 0, 0]}>
      {/* Render white keys first */}
      {allNotes.filter(note => !isBlackKey(note)).map((note, index) => {
        const isLastKey = index === allNotes.filter(note => !isBlackKey(note)).length - 1;
        
        return (
          <group key={note} position={[index * (WHITE_KEY_WIDTH + KEY_GAP), 0, 0]}>
            {/* Stroke effect - only between keys */}
            {!isLastKey && (
              <mesh position={[WHITE_KEY_WIDTH/2, 0, -0.001]}>
                <boxGeometry args={[0.04, WHITE_KEY_HEIGHT, KEY_DEPTH]} />
                <meshBasicMaterial color={`#${keyStrokeColor.getHexString()}`} />
              </mesh>
            )}
            {/* Main key */}
            <mesh>
              <boxGeometry args={[WHITE_KEY_WIDTH, WHITE_KEY_HEIGHT, KEY_DEPTH]} />
              <meshBasicMaterial color={getNoteColor(note)} />
            </mesh>
            {showNoteLetters && activeNotes.some(activeNote => {
              const activeNoteWithoutOctave = activeNote.replace(/\d+$/, '');
              const normalizedActiveNote = Note.get(activeNoteWithoutOctave).pc;
              const normalizedNote = Note.get(note.replace(/\d+$/, '')).pc;
              return normalizedActiveNote === normalizedNote;
            }) && (
              <Text
                position={[0, -WHITE_KEY_HEIGHT/2 + 0.1125, KEY_DEPTH/2 + 0.001]}
                fontSize={0.10}
                color={`#${keyStrokeColor.getHexString()}`}
                anchorX="center"
                anchorY="middle"
              >
                {getDisplayNote(note)}
              </Text>
            )}
          </group>
        );
      })}
      
      {/* Render black keys on top */}
      {allNotes.filter(note => isBlackKey(note)).map((note) => {
        const fullIndex = allNotes.indexOf(note);
        const whiteKeysBefore = allNotes.slice(0, fullIndex).filter(n => !isBlackKey(n)).length;
        const xPos = (whiteKeysBefore * (WHITE_KEY_WIDTH + KEY_GAP)) - (WHITE_KEY_WIDTH / 2);
        
        return (
          <group key={note} position={[xPos, (WHITE_KEY_HEIGHT - BLACK_KEY_HEIGHT) / 2, KEY_DEPTH/2]}>
            {/* Stroke effect - bottom, left, and right edges */}
            <mesh position={[0, -BLACK_KEY_HEIGHT/2, -0.001]}>
              <boxGeometry args={[BLACK_KEY_WIDTH + 0.025, 0.025, KEY_DEPTH]} />
              <meshBasicMaterial color={`#${keyStrokeColor.getHexString()}`} />
            </mesh>
            <mesh position={[-BLACK_KEY_WIDTH/2, 0, -0.001]}>
              <boxGeometry args={[0.025, BLACK_KEY_HEIGHT, KEY_DEPTH]} />
              <meshBasicMaterial color={`#${keyStrokeColor.getHexString()}`} />
            </mesh>
            <mesh position={[BLACK_KEY_WIDTH/2, 0, -0.001]}>
              <boxGeometry args={[0.025, BLACK_KEY_HEIGHT, KEY_DEPTH]} />
              <meshBasicMaterial color={`#${keyStrokeColor.getHexString()}`} />
            </mesh>
            {/* Main key */}
            <mesh>
              <boxGeometry args={[BLACK_KEY_WIDTH, BLACK_KEY_HEIGHT, KEY_DEPTH]} />
              <meshBasicMaterial color={getNoteColor(note)} />
            </mesh>
            {showNoteLetters && activeNotes.some(activeNote => {
              const activeNoteWithoutOctave = activeNote.replace(/\d+$/, '');
              const normalizedActiveNote = Note.get(activeNoteWithoutOctave).pc;
              const normalizedNote = Note.get(note.replace(/\d+$/, '')).pc;
              return normalizedActiveNote === normalizedNote;
            }) && (
              <Text
                position={[0, -BLACK_KEY_HEIGHT/2 + 0.10, KEY_DEPTH/2 + 0.001]}
                fontSize={0.10}
                color={getNoteColor(note) === noteColors[note.replace(/\d+$/, '')] || getNoteColor(note) === noteColors[Note.enharmonic(note.replace(/\d+$/, ''))]
                  ? `#${keyStrokeColor.getHexString()}`
                  : `#${getThreeJsColor('dark', 'base-content').getHexString()}`}
                anchorX="center"
                anchorY="middle"
              >
                {getDisplayNote(note)}
              </Text>
            )}
          </group>
        );
      })}
    </group>
  );
};

export default PianoKeys; 